import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@shared/ui/card';
import { LabeledInput, LoadingButton } from '@shared/patterns';
import { createApiClient, ApiError } from '@shared/api/client';
import { toast } from 'sonner';
import {
  GraduationCap,
  Users,
  Briefcase,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Check,
  BookOpen,
  Sparkles,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';

const api = createApiClient('/api/v1');

interface RegisterOptions {
  tenant_id: string;
  tenant_name: string;
  allow_student_self_registration: boolean;
  allow_parent_self_registration: boolean;
  allow_staff_self_registration: boolean;
  branches: Array<{ id: string; name: string; code: string }>;
  batches: Array<{ id: string; name: string; code: string }>;
}

const TARGET_EXAM_PRESETS = [
  'Class 11 - JEE Main & Advanced',
  'Class 12 - JEE Main & Advanced',
  'Class 11 - NEET UG',
  'Class 12 - NEET UG',
  'Foundation (Classes 8-10)',
  'Dropper / Repeaters Rank Booster',
];

const AVAILABLE_SUBJECTS = [
  'Physics',
  'Chemistry',
  'Mathematics',
  'Biology',
  'English',
  'Mental Ability',
];

const GUARDIAN_RELATIONS = ['Father', 'Mother', 'Local Guardian'];

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState<RegisterOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stepper state for student onboarding
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Step 1: Account Details
  const [role, setRole] = useState<'student' | 'parent' | 'teacher'>('student');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');

  // Step 2: Academic Goals (Student)
  const [targetExam, setTargetExam] = useState<string>('Class 11 - JEE Main & Advanced');
  const [grade, setGrade] = useState<string>('Class 11');
  const [subjects, setSubjects] = useState<string[]>(['Physics', 'Chemistry', 'Mathematics']);
  const [batchId, setBatchId] = useState('');

  // Step 3: Guardian Details (Student)
  const [guardianName, setGuardianName] = useState('');
  const [guardianRelation, setGuardianRelation] = useState('Father');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');

  // Other roles
  const [wardAdmissionNo, setWardAdmissionNo] = useState('');
  const [designation, setDesignation] = useState('');
  const [department, setDepartment] = useState('');

  useEffect(() => {
    async function fetchOptions() {
      try {
        const data = await api.get<RegisterOptions>('/auth/register-options');
        setOptions(data);
        if (data.allow_student_self_registration) {
          setRole('student');
        } else if (data.allow_parent_self_registration) {
          setRole('parent');
        } else if (data.allow_staff_self_registration) {
          setRole('teacher');
        }
      } catch (e: any) {
        setError(e.message || 'Unable to load registration options');
      } finally {
        setIsLoadingOptions(false);
      }
    }
    fetchOptions();
  }, []);

  const anyEnabled =
    options?.allow_student_self_registration ||
    options?.allow_parent_self_registration ||
    options?.allow_staff_self_registration;

  const toggleSubject = (sub: string) => {
    if (subjects.includes(sub)) {
      if (subjects.length === 1) {
        setError('At least one subject of interest must remain selected.');
        return;
      }
      setError(null);
      setSubjects(subjects.filter((s) => s !== sub));
    } else {
      setError(null);
      setSubjects([...subjects, sub]);
    }
  };

  const handleSelectExam = (preset: string) => {
    setTargetExam(preset);
    setError(null);
    if (preset.includes('Class 11')) {
      setGrade('Class 11');
    } else if (preset.includes('Class 12')) {
      setGrade('Class 12');
    } else if (preset.includes('Foundation')) {
      setGrade('Foundation');
    } else if (preset.includes('Dropper')) {
      setGrade('Dropper / Repeaters');
    }

    // Auto-adjust default subjects if switching between JEE and NEET
    if (preset.includes('NEET') && !subjects.includes('Biology')) {
      setSubjects((prev) => Array.from(new Set([...prev.filter((s) => s !== 'Mathematics'), 'Biology'])));
    } else if (preset.includes('JEE') && !subjects.includes('Mathematics')) {
      setSubjects((prev) => Array.from(new Set([...prev.filter((s) => s !== 'Biology'), 'Mathematics'])));
    }
  };

  const validateStep1 = (): boolean => {
    if (!fullName.trim()) {
      setError('Full name is required.');
      return false;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please provide a valid email address.');
      return false;
    }
    if (!phone.trim() || phone.trim().length < 7) {
      setError('Please provide a valid contact phone number.');
      return false;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!targetExam) {
      setError('Please select your target examination / academic stream.');
      return false;
    }
    if (subjects.length === 0) {
      setError('Please select at least one subject of interest.');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep3 = (): boolean => {
    if (!guardianName.trim()) {
      setError('Guardian full name is required.');
      return false;
    }
    if (!guardianRelation) {
      setError('Please specify the guardian relationship.');
      return false;
    }
    if (!guardianPhone.trim() || guardianPhone.trim().length < 7) {
      setError('Guardian contact phone number is required.');
      return false;
    }
    if (guardianEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guardianEmail.trim())) {
      setError('Guardian email address is invalid.');
      return false;
    }
    setError(null);
    return true;
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(3);
      }
    }
  };

  const handlePrevStep = () => {
    setError(null);
    if (currentStep === 3) setCurrentStep(2);
    else if (currentStep === 2) setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (role === 'student') {
      if (currentStep === 1) {
        if (!validateStep1()) return;
        setCurrentStep(2);
        return;
      }
      if (currentStep === 2) {
        if (!validateStep2()) return;
        setCurrentStep(3);
        return;
      }
      if (!validateStep3()) {
        return;
      }
    } else {
      if (!fullName.trim() || !email.trim() || !phone.trim() || !password) {
        setError('All standard account fields are required.');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload: Record<string, any> = {
        role,
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
      };

      if (role === 'student') {
        payload.grade = grade || (targetExam.includes('Class 11') ? 'Class 11' : targetExam.includes('Class 12') ? 'Class 12' : targetExam.includes('Foundation') ? 'Foundation' : 'Dropper');
        payload.target_exam = targetExam;
        payload.subjects = subjects;
        payload.guardian_name = guardianName.trim();
        payload.guardian_relation = guardianRelation;
        payload.guardian_phone = guardianPhone.trim();
        if (guardianEmail.trim()) {
          payload.guardian_email = guardianEmail.trim();
        }
        if (batchId) {
          payload.batch_id = batchId;
        }
      } else if (role === 'parent') {
        if (wardAdmissionNo) {
          payload.ward_admission_no = wardAdmissionNo;
        }
      } else if (role === 'teacher') {
        payload.designation = designation || 'Faculty Educator';
        payload.department = department || 'Academic';
      }

      const res = await api.post<{
        tokens?: { access_token: string; refresh_token: string };
        user?: any;
        message?: string;
      }>('/auth/register', payload);

      if (res.tokens?.access_token) {
        localStorage.setItem('hc_token', res.tokens.access_token);
        localStorage.setItem('token', res.tokens.access_token);
        localStorage.setItem('hc_role', role);
        if (res.tokens.refresh_token) {
          localStorage.setItem('hc_refresh_token', res.tokens.refresh_token);
        }
        if (role === 'student' && targetExam) {
          localStorage.setItem('helios_student_target_exam', targetExam);
        }

        toast.success(`Welcome to Helios Academy, ${fullName.trim()}! Launching your student dashboard...`);
        setSuccessMsg('Account created successfully! Directing to student dashboard...');

        setTimeout(() => {
          window.location.href = '/sis/dashboard';
        }, 500);
      } else {
        toast.success('Registration submitted successfully! You can now log in.');
        setSuccessMsg('Registration submitted successfully! You can now log in.');
        setTimeout(() => {
          navigate('/login');
        }, 1200);
      }
    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else if (err && typeof err === 'object' && 'message' in err) {
        setError(String(err.message));
      } else {
        setError('Registration failed. Please verify your details and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingOptions) {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center justify-center space-y-3">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground">Checking registration status...</p>
        </CardContent>
      </Card>
    );
  }

  if (!anyEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Registration Closed</CardTitle>
          <CardDescription>
            {options?.tenant_name || 'Institute'} has self-registration disabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Direct self-registration is currently restricted. Please contact your institute administrator or counselor to obtain an admission or login account.
            </div>
          </div>
          <div className="text-center pt-2">
            <Link to="/login" className="text-sm text-primary font-medium hover:underline">
              Return to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  const stepsList = [
    { num: 1, label: 'Account Details', desc: 'Credentials' },
    { num: 2, label: 'Academic Goals', desc: 'Target Exam' },
    { num: 3, label: 'Guardian Details', desc: 'Contact Info' },
  ];

  return (
    <Card className="shadow-lg border-border/80">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">Student Onboarding & Registration</CardTitle>
            <CardDescription>
              Join {options?.tenant_name || 'Helios Academy'} unified learning portal.
            </CardDescription>
          </div>
          {role === 'student' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              <Sparkles className="w-3.5 h-3.5" />
              Step {currentStep} of 3
            </span>
          )}
        </div>

        {/* Role Switcher Tabs */}
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-muted rounded-lg mt-3">
          {options?.allow_student_self_registration && (
            <button
              type="button"
              onClick={() => {
                setRole('student');
                setError(null);
              }}
              className={`min-h-[44px] flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-all ${
                role === 'student'
                  ? 'bg-card text-foreground shadow-xs font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <GraduationCap className="w-4 h-4 shrink-0" />
              <span>Student</span>
            </button>
          )}
          {options?.allow_parent_self_registration && (
            <button
              type="button"
              onClick={() => {
                setRole('parent');
                setError(null);
              }}
              className={`min-h-[44px] flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-all ${
                role === 'parent'
                  ? 'bg-card text-foreground shadow-xs font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="w-4 h-4 shrink-0" />
              <span>Parent</span>
            </button>
          )}
          {options?.allow_staff_self_registration && (
            <button
              type="button"
              onClick={() => {
                setRole('teacher');
                setError(null);
              }}
              className={`min-h-[44px] flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md transition-all ${
                role === 'teacher'
                  ? 'bg-card text-foreground shadow-xs font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Briefcase className="w-4 h-4 shrink-0" />
              <span>Faculty</span>
            </button>
          )}
        </div>

        {/* 3-Step Progress Stepper for Student Role */}
        {role === 'student' && (
          <div className="pt-4 pb-2" aria-label="Registration Progress">
            <div className="grid grid-cols-3 gap-2 relative">
              {stepsList.map((step) => {
                const isCompleted = currentStep > step.num;
                const isActive = currentStep === step.num;

                return (
                  <div
                    key={step.num}
                    className={`flex flex-col items-center text-center p-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary/10 border border-primary/30 text-foreground font-semibold'
                        : isCompleted
                        ? 'bg-muted/40 text-muted-foreground border border-transparent'
                        : 'text-muted-foreground opacity-60 border border-transparent'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all mb-1 ${
                        isCompleted
                          ? 'bg-primary text-primary-foreground'
                          : isActive
                          ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                          : 'bg-muted text-muted-foreground border border-border'
                      }`}
                    >
                      {isCompleted ? <Check className="w-4 h-4 stroke-[3]" /> : step.num}
                    </div>
                    <span className="text-xs font-medium leading-tight">{step.label}</span>
                    <span className="text-[10px] text-muted-foreground hidden md:inline leading-tight">
                      {step.desc}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              role="alert"
              className="p-3.5 rounded-lg bg-destructive/15 border border-destructive/30 text-xs text-destructive font-medium flex items-center gap-2.5 animate-in fade-in"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div
              role="status"
              className="p-3.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-2.5 animate-in fade-in"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* STUDENT 3-STEP WIZARD */}
          {role === 'student' && (
            <>
              {/* STEP 1: Account Details */}
              {currentStep === 1 && (
                <div className="space-y-3.5">
                  <div className="border-b pb-2 mb-2">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-primary" />
                      Step 1: Student Account Details
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Enter your personal credentials to create your secure student identity.
                    </p>
                  </div>

                  <LabeledInput
                    label="Full Name"
                    type="text"
                    required
                    value={fullName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                    placeholder="e.g. Aarav Sharma"
                    aria-label="Student Full Name"
                  />

                  <LabeledInput
                    label="Email Address"
                    type="email"
                    required
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    placeholder="student@example.com"
                    aria-label="Student Email Address"
                  />

                  <LabeledInput
                    label="Contact Phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    aria-label="Student Contact Phone"
                  />

                  <LabeledInput
                    label="Password (minimum 8 characters)"
                    type="password"
                    required
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    placeholder="Create a strong password (min 8 chars)"
                    aria-label="Student Password"
                  />

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-semibold text-xs rounded-md shadow-sm hover:bg-primary/90 transition-all"
                    >
                      <span>Continue to Academic Goals</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Academic Goals */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="border-b pb-2 mb-2">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" />
                      Step 2: Academic Goals & Target Stream
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Select your target competitive examination and key subject interests.
                    </p>
                  </div>

                  {/* Target Exam Presets */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-foreground block">
                      Target Exam & Grade Stream <span className="text-destructive">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {TARGET_EXAM_PRESETS.map((preset) => {
                        const isSelected = targetExam === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => handleSelectExam(preset)}
                            className={`min-h-[44px] p-3 text-left rounded-lg border text-xs font-medium transition-all flex items-center justify-between gap-2 ${
                              isSelected
                                ? 'border-primary bg-primary/10 text-primary font-semibold ring-2 ring-primary/30 shadow-xs'
                                : 'border-border bg-card hover:bg-muted/50 text-foreground'
                            }`}
                          >
                            <span>{preset}</span>
                            {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Subjects of Interest Interactive Chips */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-foreground">
                        Subjects of Interest <span className="text-destructive">*</span>
                      </label>
                      <span className="text-[11px] text-muted-foreground">Select one or more</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_SUBJECTS.map((sub) => {
                        const isSelected = subjects.includes(sub);
                        return (
                          <button
                            key={sub}
                            type="button"
                            onClick={() => toggleSubject(sub)}
                            className={`min-h-[44px] px-4 py-2 rounded-full border text-xs font-medium transition-all flex items-center gap-1.5 ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary shadow-xs font-semibold'
                                : 'bg-muted/40 hover:bg-muted text-muted-foreground border-border'
                            }`}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                            <span>{sub}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Optional Cohort / Batch */}
                  {options?.batches && options.batches.length > 0 && (
                    <div className="space-y-1 text-left">
                      <label className="text-xs font-medium text-foreground">
                        Preferred Cohort / Batch (Optional)
                      </label>
                      <select
                        value={batchId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBatchId(e.target.value)}
                        className="w-full text-xs min-h-[44px] rounded-md border border-input bg-background px-3 py-1 shadow-xs focus:ring-1 focus:ring-ring"
                      >
                        <option value="">Select an academic batch...</option>
                        {options.batches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name} ({b.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex items-center gap-2.5 pt-2">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="min-h-[44px] px-4 py-2.5 rounded-md border border-input bg-background hover:bg-muted font-semibold text-xs transition-colors flex items-center gap-1.5"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="flex-1 min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-semibold text-xs rounded-md shadow-sm hover:bg-primary/90 transition-all"
                    >
                      <span>Continue to Guardian Details</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Guardian Details */}
              {currentStep === 3 && (
                <div className="space-y-3.5">
                  <div className="border-b pb-2 mb-2">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-primary" />
                      Step 3: Guardian Details & Family Link
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Helios links each learner with a verified primary guardian for security and performance reports.
                    </p>
                  </div>

                  <LabeledInput
                    label="Guardian Full Name"
                    type="text"
                    required
                    value={guardianName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuardianName(e.target.value)}
                    placeholder="e.g. Ramesh Sharma"
                    aria-label="Guardian Full Name"
                  />

                  {/* Relationship selector */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-foreground">
                      Relationship <span className="text-destructive">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {GUARDIAN_RELATIONS.map((rel) => {
                        const isSelected = guardianRelation === rel;
                        return (
                          <button
                            key={rel}
                            type="button"
                            onClick={() => setGuardianRelation(rel)}
                            className={`min-h-[44px] p-2.5 text-center rounded-lg border text-xs font-medium transition-all ${
                              isSelected
                                ? 'border-primary bg-primary/10 text-primary font-semibold ring-2 ring-primary/30 shadow-xs'
                                : 'border-border bg-card hover:bg-muted/50 text-foreground'
                            }`}
                          >
                            {rel}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <LabeledInput
                    label="Guardian Contact Phone"
                    type="tel"
                    required
                    value={guardianPhone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuardianPhone(e.target.value)}
                    placeholder="+91 98765 88888"
                    aria-label="Guardian Contact Phone"
                  />

                  <LabeledInput
                    label="Guardian Email Address (Optional)"
                    type="email"
                    value={guardianEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuardianEmail(e.target.value)}
                    placeholder="guardian@example.com"
                    aria-label="Guardian Email"
                  />

                  {/* Onboarding Summary Box */}
                  <div className="p-3 rounded-lg bg-muted/60 border border-border/80 text-xs space-y-1.5">
                    <div className="font-semibold text-foreground flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary" />
                      <span>Onboarding Summary:</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[11px] text-muted-foreground">
                      <div><strong className="text-foreground">Student:</strong> {fullName || '—'}</div>
                      <div><strong className="text-foreground">Exam:</strong> {targetExam}</div>
                      <div><strong className="text-foreground">Subjects:</strong> {subjects.join(', ')}</div>
                      <div><strong className="text-foreground">Guardian:</strong> {guardianName || '—'} ({guardianRelation})</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 pt-2">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="min-h-[44px] px-4 py-2.5 rounded-md border border-input bg-background hover:bg-muted font-semibold text-xs transition-colors flex items-center gap-1.5"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                    <LoadingButton
                      type="submit"
                      onClick={handleSubmit}
                      className="flex-1 min-h-[44px] text-xs font-semibold"
                      isLoading={isSubmitting}
                    >
                      Complete Enrollment & Launch Dashboard 🚀
                    </LoadingButton>
                  </div>
                </div>
              )}
            </>
          )}

          {/* PARENT / FACULTY ROLES */}
          {role !== 'student' && (
            <div className="space-y-3.5">
              <LabeledInput
                label="Full Name"
                type="text"
                required
                value={fullName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                placeholder={role === 'parent' ? 'e.g. Rajesh Sharma' : 'e.g. Dr. Priya Verma'}
              />

              <LabeledInput
                label="Email Address"
                type="email"
                required
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />

              <LabeledInput
                label="Mobile Phone"
                type="tel"
                required
                value={phone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
              />

              <LabeledInput
                label="Password (minimum 6 characters)"
                type="password"
                required
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
              />

              {role === 'parent' && (
                <LabeledInput
                  label="Child's Admission Number (Optional)"
                  type="text"
                  value={wardAdmissionNo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWardAdmissionNo(e.target.value)}
                  placeholder="e.g. ADM-2026-0001 (auto-links child account)"
                />
              )}

              {role === 'teacher' && (
                <>
                  <LabeledInput
                    label="Designation"
                    type="text"
                    value={designation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDesignation(e.target.value)}
                    placeholder="e.g. Senior Physics Faculty"
                  />
                  <LabeledInput
                    label="Department"
                    type="text"
                    value={department}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDepartment(e.target.value)}
                    placeholder="e.g. Science / Mathematics"
                  />
                </>
              )}

              <LoadingButton
                type="submit"
                onClick={handleSubmit}
                className="w-full min-h-[44px] mt-2 text-xs font-semibold"
                isLoading={isSubmitting}
              >
                Complete Registration
              </LoadingButton>
            </div>
          )}

          <div className="text-center pt-2 text-xs text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign In
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
